import { useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Check, ChevronDown, ChevronRight, LoaderCircle } from 'lucide-react';
import { Badge } from '@radix-ui/themes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
// import { Alert, AlertSeverityType } from '@/types/alerts';
import { generateAlertMessage, getAlertSeverityColorClasses, getAlertStatusColorClasses } from '@/utils/alert-utils';
import { EmptyState } from '@/components/ui/empty-state';
import { GetAlertsApiResponse } from '@/services';
import { AlertSeverityType } from '@/types/alerts';

export type AlertListProps = {
  alerts: GetAlertsApiResponse['data']['items'];
  isLoading: boolean;
  onAcknowledge: (alertId: string) => void;
  onResolve?: (alertId: string) => void;
  selectedSeverity: string;
  onChangeSeverity: (severity: string) => void;
  showResolveButton?: boolean;
  isActionLoading?: boolean;
};

export const AlertList = ({
  alerts,
  isLoading,
  onAcknowledge,
  onResolve,
  selectedSeverity,
  onChangeSeverity,
  showResolveButton = false,
  isActionLoading = false,
}: AlertListProps) => {
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  // Filter alerts based on selected severity
  const filteredAlerts = alerts.filter((alert) => {
    if (selectedSeverity === 'all') return true;
    return alert.severity === selectedSeverity;
  });

  // Count alerts by severity
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const highCount = alerts.filter((a) => a.severity === 'high').length;
  const mediumCount = alerts.filter((a) => a.severity === 'medium').length;
  const lowCount = alerts.filter((a) => a.severity === 'low').length;
  const infoCount = alerts.filter((a) => a.severity === 'info').length;

  // Pagination
  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
  const paginatedAlerts = filteredAlerts.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const toggleExpandAlert = (alertId: string) => {
    setExpandedAlertId(expandedAlertId === alertId ? null : alertId);
  };

  // Skeleton component for loading state
  if (isLoading) {
    return <AlertListSkeleton />;
  }

  return (
    <Card className="overflow-hidden shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            <Tabs value={selectedSeverity} onValueChange={onChangeSeverity} className="w-full">
              <TabsList className="flex gap-2">
                <TabsTrigger
                  value="all"
                  className="px-3 py-1.5 rounded-md data-[state=active]:bg-muted data-[state=active]:shadow-sm"
                >
                  All
                  {!!alerts.length && (
                    <Badge radius="full" className="ml-2" color="gray" size="1">
                      {alerts.length}
                    </Badge>
                  )}
                </TabsTrigger>

                {criticalCount > 0 && (
                  <TabsTrigger
                    value="critical"
                    className="px-3 py-1.5 rounded-md data-[state=active]:bg-muted data-[state=active]:shadow-sm"
                  >
                    Critical
                    <Badge radius="full" className="ml-2" color="red" size="1">
                      {criticalCount}
                    </Badge>
                  </TabsTrigger>
                )}

                {highCount > 0 && (
                  <TabsTrigger
                    value="high"
                    className="px-3 py-1.5 rounded-md data-[state=active]:bg-muted data-[state=active]:shadow-sm"
                  >
                    High
                    <Badge radius="full" className="ml-2" color="orange" size="1">
                      {highCount}
                    </Badge>
                  </TabsTrigger>
                )}

                {mediumCount > 0 && (
                  <TabsTrigger
                    value="medium"
                    className="px-3 py-1.5 rounded-md data-[state=active]:bg-muted data-[state=active]:shadow-sm"
                  >
                    Medium
                    <Badge radius="full" className="ml-2" color="yellow" size="1">
                      {mediumCount}
                    </Badge>
                  </TabsTrigger>
                )}

                {lowCount > 0 && (
                  <TabsTrigger
                    value="low"
                    className="px-3 py-1.5 rounded-md data-[state=active]:bg-muted data-[state=active]:shadow-sm"
                  >
                    Low
                    <Badge radius="full" className="ml-2" color="blue" size="1">
                      {lowCount}
                    </Badge>
                  </TabsTrigger>
                )}

                {infoCount > 0 && (
                  <TabsTrigger
                    value="info"
                    className="px-3 py-1.5 rounded-md data-[state=active]:bg-muted data-[state=active]:shadow-sm"
                  >
                    Info
                    <Badge radius="full" className="ml-2" color="green" size="1">
                      {infoCount}
                    </Badge>
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {alerts.length === 0 ? (
          <EmptyState
            icon={Check}
            title="No alerts found"
            description="Everything is running normally"
            iconClassName="text-green-500 bg-green-50 dark:bg-green-950/30"
          />
        ) : filteredAlerts.length === 0 ? (
          <EmptyState
            title="No alerts matching selected filter"
            description="Try selecting a different filter to see more alerts"
            iconClassName="hidden"
          />
        ) : (
          <>
            <div className="divide-y">
              {paginatedAlerts.map((alert) => (
                <div key={alert.id} className="p-4 hover:bg-muted/50">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-3">
                      {/* Alert icon based on severity */}
                      <AlertIcon severity={alert.severity} />

                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{generateAlertMessage(alert)}</p>
                          <AlertStatusBadge status={alert.status} />
                        </div>

                        <p className="text-sm text-muted-foreground">
                          {alert.device?.name || 'Device'} • {format(new Date(alert.triggered_at), 'MMM d, h:mm a')}
                        </p>

                        <div className="mt-2 text-sm">
                          <span className="text-muted-foreground">Sensor:</span>{' '}
                          <span className="font-medium">{alert.sensor?.name || 'Unknown'}</span>
                          <span className="mx-1 text-muted-foreground">•</span>
                          <span className="text-muted-foreground">Value:</span>{' '}
                          <span className="font-medium">
                            {typeof alert.triggered_value === 'number'
                              ? alert.triggered_value.toFixed(2)
                              : JSON.stringify(alert.triggered_value)}
                            {alert.sensor?.unit}
                          </span>
                        </div>

                        {/* Expanded alert details */}
                        {expandedAlertId === alert.id && (
                          <div className="mt-3 p-3 bg-muted/30 rounded-md text-sm">
                            <div className="grid grid-cols-1 gap-2">
                              <div>
                                <span className="font-medium">Alert Rule:</span>{' '}
                                <span>{alert.alert_rule?.name || 'Unknown'}</span>
                              </div>
                              {alert.alert_rule?.description && (
                                <div>
                                  <span className="font-medium">Description:</span>{' '}
                                  <span>{alert.alert_rule.description}</span>
                                </div>
                              )}
                              <div>
                                <span className="font-medium">Triggered:</span>{' '}
                                <span>{format(new Date(alert.triggered_at), 'MMM d, yyyy h:mm:ss a')}</span>
                              </div>
                              {alert.acknowledged_at && (
                                <div>
                                  <span className="font-medium">Acknowledged:</span>{' '}
                                  <span>
                                    {format(new Date(alert.acknowledged_at), 'MMM d, yyyy h:mm:ss a')} by{' '}
                                    {alert.acknowledged_by_user?.first_name || 'Unknown'}
                                  </span>
                                </div>
                              )}
                              {alert.resolved_at && (
                                <div>
                                  <span className="font-medium">Resolved:</span>{' '}
                                  <span>{format(new Date(alert.resolved_at), 'MMM d, yyyy h:mm:ss a')}</span>
                                </div>
                              )}
                              {alert.note && (
                                <div>
                                  <span className="font-medium">Note:</span> <span>{alert.note}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <AlertSeverityBadge severity={alert.severity} />

                      <div className="flex gap-1 mt-2">
                        {/* Action buttons based on alert state */}
                        {alert.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => onAcknowledge(alert.id)}
                            disabled={isActionLoading}
                          >
                            {isActionLoading ? (
                              <LoaderCircle className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4 mr-1" />
                            )}
                            Acknowledge
                          </Button>
                        )}

                        {showResolveButton && onResolve && ['active', 'acknowledged'].includes(alert.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => onResolve(alert.id)}
                            disabled={isActionLoading}
                          >
                            {isActionLoading ? (
                              <LoaderCircle className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4 mr-1" />
                            )}
                            Resolve
                          </Button>
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => toggleExpandAlert(alert.id)}
                        >
                          {expandedAlertId === alert.id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-3 flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="py-2 px-3 text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Loading Skeleton Component
const AlertListSkeleton = () => (
  <Card>
    <CardHeader>
      <div className="flex items-center gap-3 overflow-x-auto pb-1">
        <Skeleton className="h-[38px] w-[200px] rounded-md" />
      </div>
    </CardHeader>
    <CardContent>
      {[1, 2, 3].map((i) => (
        <div key={i} className="mb-6">
          <div className="flex justify-between mb-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div>
                <Skeleton className="h-5 w-64 mb-1" />
                <Skeleton className="h-4 w-40" />
              </div>
            </div>
            <div>
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
          </div>
          <Skeleton className="h-4 w-full mt-2" />
        </div>
      ))}
    </CardContent>
  </Card>
);

// Alert Icon Component
const AlertIcon = ({ severity }: { severity: AlertSeverityType }) => {
  const colors = getAlertSeverityColorClasses(severity);

  return (
    <div
      className={`
        w-8 h-8 rounded-full flex items-center justify-center
        ${colors.bgLight} ${colors.textLight} ${colors.bgDark} ${colors.textDark}
      `}
    >
      <AlertTriangle className="h-4 w-4" />
    </div>
  );
};

// Alert Status Badge Component
const AlertStatusBadge = ({ status }: { status: string }) => {
  const colors = getAlertStatusColorClasses(status);
  const label = status.charAt(0).toUpperCase() + status.slice(1);

  return <Badge className={`text-xs px-1.5 py-0.5 rounded-md ${colors.bg} ${colors.text}`}>{label}</Badge>;
};

// Alert Severity Badge Component
const AlertSeverityBadge = ({ severity }: { severity: AlertSeverityType }) => {
  const label = severity.charAt(0).toUpperCase() + severity.slice(1);

  // Set color based on severity
  const getBadgeBgColor = () => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500 text-white';
      case 'high':
        return 'bg-orange-500 text-white';
      case 'medium':
        return 'bg-yellow-500 text-white';
      case 'low':
        return 'bg-blue-500 text-white';
      case 'info':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return <Badge className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getBadgeBgColor()}`}>{label}</Badge>;
};
